import { memo, useState } from 'react';
import { useReduxState } from 'hooks';
import { useRouter } from 'next/router';
import { breakpoints } from 'styles';
import { Pagination } from 'components/common';
import { Box } from 'components/common/box';
import { useEditorDispatch } from 'components/editor';
import { EditorHeaderChapter } from 'components/editor/header';
import {
  EditorVersionHistoryFilters,
  EditorVersionHistoryTableChapter,
  EditorVersionHistoryTableCourse
} from '.';

import type { Editor } from 'types';
import type { EditorHeaderChapterStateProps } from 'components/editor/header/EditorHeaderChapter';

interface Props extends EditorHeaderChapterStateProps {
  activeView: Editor['editorActiveView'];
  currentChapter: Editor['editorCurrentChapter'];
}

const EditorVersionHistoryTable = ({
  activeView,
  currentChapter,
  ...stateProps
}: Props) => {
  const editorDispatch = useEditorDispatch();

  const { messages } = useReduxState();

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [numberOfPages, setNumberOfPages] = useState(1);
  const [page, setPage] = useState(1);

  const router = useRouter();

  const onDateRangeChange = ({ fromDate: from, toDate: to }) => {
    setFromDate(from);
    setToDate(to);
  };

  const handlePreviewInEditor = async (url: string) => {
    router.push(url, url, { shallow: true });
    editorDispatch({
      type: 'updateContext',
      update: { editorActiveView: 'edit', editorIsFetching: true }
    });
  };

  const isCourseView = activeView === 'course-version-history';

  const queryParams = `page=${page}&fromDate=${fromDate}&toDate=${toDate}`;

  return (
    <div className="content">
      <Box
        headerContent={
          isCourseView ? (
            <EditorVersionHistoryFilters
              onDateRangeChange={onDateRangeChange}
              title={messages.coursesCourseVersionHistory}
            />
          ) : (
            <div style={{ fontSize: '1.8rem', marginTop: 5 }}>
              <EditorHeaderChapter
                currentChapter={currentChapter}
                {...stateProps}
              />
            </div>
          )
        }
        headerContentWrap={true}
        padding={0}
        subHeaderContent={
          isCourseView ? null : (
            <EditorVersionHistoryFilters
              onDateRangeChange={onDateRangeChange}
              title={messages.coursesChapterVersionHistory}
            />
          )
        }
      >
        {isCourseView ? (
          <EditorVersionHistoryTableCourse
            onPreview={handlePreviewInEditor}
            onPagesChange={setNumberOfPages}
            queryParams={queryParams}
          />
        ) : (
          <EditorVersionHistoryTableChapter
            chapterId={currentChapter.id}
            chapterSlug={currentChapter.slug}
            onPagesChange={setNumberOfPages}
            onPreview={handlePreviewInEditor}
            queryParams={queryParams}
          />
        )}
      </Box>

      <Pagination
        currentPage={page}
        numberOfPages={numberOfPages}
        onPageChange={({ page }) => setPage(page)}
      />

      <style jsx>{`
        .content {
          position: relative;
        }
        @media (max-width: ${breakpoints.medium}px) {
          .content {
            margin-top: 20px;
          }
        }
      `}</style>
    </div>
  );
};

export default memo(EditorVersionHistoryTable);
